import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Breadcrumb, BackdropCircular, ConfirmDialog } from 'components';
import NumberFormat from 'react-number-format';
import {
  Button,
  Icon,
  TextField,
  Card,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  Grid,
  Switch,
  Typography,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import {
  getBrands,
  getStoreCategoriesApi,
  getStoreCategoriesTreeApi,
  getStoreTaxClassesApi,
  addTasksToQueueApi,
} from 'api';
import clsx from 'clsx';
import { addToMassEdit } from 'redux/actions/products';
import {
  changeProductCategoryStatus
} from 'redux/actions/categories';
import { generateTaskToDeleteProduct } from 'helpers/queueTasks';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCategoryModal from './addCategoryModal';


const MassEditProducts = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    user,
    brandsData,
    loading,
    loadingTasksToQueue,
    savingProduct,
    massEdit,
    storeCategoriesWithActiveNavLevel,
    storeCategoriesTree,
    userProfile,
    storeTaxClasses
  } = useSelector((state: IReduxState) => ({
    user: state.user,
    brandsData: state.brands.brandsData,
    loading: state.brands.loading || state.categories.loadingCategories,
    savingProduct: state.products.savingProduct,
    massEdit: state.products.massEdit,
    storeCategories: state.categories.storeCategories,
    storeCategoriesWithActiveNavLevel: state.categories.storeCategoriesWithActiveNavLevel,
    storeCategoriesTree: state.categories.storeCategoriesTree,
    userStoreIsCreated: state.user.userStoreIsCreated,
    userProfile: state.user.profile,
    storeTaxClasses: state.products.storeTaxClasses,
    loadingTasksToQueue: state.products.loadingTasksToQueue
  }));

  const userStore = user.profile?.stores?.[0];
  const permissions = userProfile?.config?.permissions || {};
  const taxableClass = storeTaxClasses.find((c) => c.class_name.toLowerCase().includes(userProfile?.stores?.[0]?.storeCode));

  const [massEditList, setMassEditList] = useState(massEdit.list);
  const [editedProducts, setEditedProducts] = useState([]);
  const [changedProductIds, setChangedProductsIds] = useState([]);
  const [productIdxToAddCategory, setProductIdxToAddCategory] = useState();
  const [openAddCategoryModel, setOpenAddCategoryModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isAllEnabled, setIsAllEnabled] = useState(!!massEdit.list.find((p) => p.product.status == 1));

  const isPriceLowerThanMin = massEditList.find((item) => {
    const {product} = item;
    const minProductPrice = Number(product?.custom_attributes?.find((attr) => attr.attribute_code === 'catalog_min_price')?.value);
    const lessThanMinPrice = Boolean(minProductPrice && product.price < minProductPrice);

    return lessThanMinPrice;
  });

  if (!massEditList.length) {
    history.push('/store-products/list/active');
  }

  const isEmptyProductName = massEditList.find((product) => product.product.name.trim().length === 0);

  useEffect(() => {
    if (!brandsData.length) {
      getBrands({dispatch});
    }

    if (!storeCategoriesWithActiveNavLevel.length) {
      getStoreCategoriesApi({dispatch, checkStoreType: false});
    }

    if (!storeCategoriesTree.length) {
      getStoreCategoriesTreeApi({dispatch});
    }

    getStoreTaxClassesApi({dispatch});

    return () => {
      dispatch(addToMassEdit([]));
    };
  }, []);


  const handleChangeName = (productIndex, name) => {
    const {product: editedProduct} = massEditList[productIndex];

    const changedProductData = { sku: editedProduct.sku, name };
    const alreadyEdited = editedProducts.find((p) => p.sku === changedProductData.sku);

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    setChangedProductsIds([...new Set([...changedProductIds, productIndex])]);

    const updatedList = massEditList.map((item, idx) => {
      if (idx == productIndex) {
        const {product = {}} = item;
        return {...item, product: {...product, name}};
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleRemoveCategory = (productIndex, categoryId) => {
    const {product: editedProduct} = massEditList[productIndex];
    const categoryIds = editedProduct.custom_attributes.find((a) => a.attribute_code === 'category_ids');
    const alreadyEdited = editedProducts.find((p) => p.sku === editedProduct.sku);
    const otherAttributes = alreadyEdited?.custom_attributes?.filter((a) => a.attribute_code !== 'category_ids') || [];

    const changedProductData = { sku: editedProduct.sku, custom_attributes: [...otherAttributes, {attribute_code: 'category_ids', value: categoryIds.value.filter((catId) => catId != categoryId)}] };

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    setChangedProductsIds([...new Set([...changedProductIds, productIndex])]);

    const updatedList = massEditList.map((item, idx) => {
      if (idx === productIndex) {
        const {product = {}} = item;
        const {custom_attributes = []} = product;
        const updatedAttributes = custom_attributes.map((attribute) => {
          if (attribute.attribute_code === 'category_ids') {
            const newValue = attribute.value.filter((catId) => catId != categoryId);
            return {...attribute, value: newValue};
          }

          return attribute;
        });

        return {...item, product: { ...product, custom_attributes: updatedAttributes } };
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleChangeStatus = (productIndex, status) => {
    const {product: editedProduct} = massEditList[productIndex];

    const changedProductData = { sku: editedProduct.sku, status };
    const alreadyEdited = editedProducts.find((p) => p.sku === changedProductData.sku);

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    setChangedProductsIds([...new Set([...changedProductIds, productIndex])]);

    const updatedList = massEditList.map((item, idx) => {
      if (idx == productIndex) {
        const {product = {}} = item;
        return {...item, product: { ...product, status } };
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleChangeStatusAll = (event, status) => {
    status = status ? 1 : 2;

    setEditedProducts((editedProducts) => {
      return massEditList.map((p) => {
        const alreadyEdited = editedProducts.find((ep) => ep.sku === p.product.sku);

        if (alreadyEdited) {
          return {...alreadyEdited, status};
        }

        return {sku: p.product.sku, status};
      });
    });
    setChangedProductsIds(massEditList.map((l, idx) => idx));
    setMassEditList((massEditList) => massEditList.map((p) => {
      const {product = {}} = p;
      return {...p, product: { ...product, status }};
    }));
    setIsAllEnabled(status == 1);
  };

  const handleChangePrice = (val, dataIndex, minProductPrice) => {
    const {product: editedProduct} = massEditList[dataIndex];

    if (!minProductPrice || val.floatValue > minProductPrice) {
      const changedProductData = { sku: editedProduct.sku, price: val.floatValue };
      const alreadyEdited = editedProducts.find((p) => p.sku === changedProductData.sku);

      if (alreadyEdited) {
        setEditedProducts((editedProducts) => editedProducts.map((p) => {
          return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
        }));
      } else {
        setEditedProducts([...editedProducts, changedProductData]);
      }

      setChangedProductsIds([...new Set([...changedProductIds, dataIndex])]);
    }


    const updatedList = massEditList.map((item, idx) => {
      if (idx === dataIndex) {
        return {...item, product: { ...item.product, price: val.floatValue } };
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleChangeQty = (val, dataIndex) => {
    const {product: editedProduct} = massEditList[dataIndex];

    const changedProductData = {
      sku: editedProduct.sku,
      extension_attributes: {
        stock_item: {
          qty: val.floatValue || 0,
        }
      }
    };
    const alreadyEdited = editedProducts.find((p) => p.sku === changedProductData.sku);

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    setChangedProductsIds([...new Set([...changedProductIds, dataIndex])]);


    const updatedList = massEditList.map((item, idx) => {
      if (idx === dataIndex) {
        return {
          ...item,
          product: {
            ...item.product,
            extension_attributes: {
              ...item.product.extension_attributes,
              stock_item: {
                ...item.product.extension_attributes.stock_item,
                qty: val.floatValue || 0,
              },
            },
          },
        };
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleChangeProductTaxClass = (productIndex, isEnabled) => {
    const {product: editedProduct} = massEditList[productIndex];
    const alreadyEdited = editedProducts.find((p) => p.sku === editedProduct.sku);
    const otherAttributes = alreadyEdited?.custom_attributes?.filter((a) => a.attribute_code !== 'tax_class_id') || [];
    const changedProductData = { sku: editedProduct.sku, custom_attributes: [...otherAttributes, {attribute_code: 'tax_class_id', value: isEnabled ? String(taxableClass?.class_id) : 0}] };

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    setChangedProductsIds([...new Set([...changedProductIds, productIndex])]);

    const updatedList = massEditList.map((item, idx) => {
      if (idx == productIndex) {
        const {product = {}} = item;
        const updatedAttributes = product.custom_attributes.map((item) => {
          if (item.attribute_code === 'tax_class_id') {
            return {...item, value: isEnabled ? String(taxableClass?.class_id) : 0};
          }

          return item;
        });

        return {...item, product: { ...product, custom_attributes: updatedAttributes } };
      }

      return item;
    });

    setMassEditList(updatedList);
  };

  const handleClickSave = async (productIndex) => {
    const {product} = massEditList[productIndex];
    const productImgAttr = product.custom_attributes?.find((attr) => attr.attribute_code === 'image');
    let editedProductData = editedProducts.find((p) => p.sku === product.sku);
    const editedAttrWithoutImg = editedProductData?.custom_attributes?.filter((a) => a?.attribute_code !== 'image') || [{ attribute_code: 'image', value: 'no_selection' }];

    if (productImgAttr) {
      editedAttrWithoutImg.push(productImgAttr);
    }

    editedProductData = {...editedProductData, custom_attributes: editedAttrWithoutImg};


    const task = {
      sku: product.sku,
      name: product.name,
      body: editedProductData,
      requestType: 'UPDATE_PRODUCT'
    };

    await addTasksToQueueApi({ dispatch, tasks: [task] });

    setChangedProductsIds(changedProductIds.filter((id) => id !== productIndex));
    setEditedProducts((editedProducts) => editedProducts.filter((p) => p.sku !== editedProductData.sku));
  };

  const handleSaveAll = async () => {
    const tasks = changedProductIds.map((id) => {
      const {product} = massEditList[id];
      const productImgAttr = product.custom_attributes?.find((attr) => attr.attribute_code === 'image');
      let editedProductData = editedProducts.find((p) => p.sku === product.sku);
      const editedAttrWithoutImg = editedProductData?.custom_attributes?.filter((a) => a?.attribute_code !== 'image') || [{ attribute_code: 'image', value: 'no_selection' }];

      if (productImgAttr) {
        editedAttrWithoutImg.push(productImgAttr);
      }

      editedProductData = {...editedProductData, custom_attributes: editedAttrWithoutImg};

      const task = {
        sku: product.sku,
        name: product.name,
        body: editedProductData,
        requestType: 'UPDATE_PRODUCT'
      };

      return task;
    });

    await addTasksToQueueApi({ dispatch, tasks });

    history.goBack();
  };

  const handleSelectCategories = (category) => {
    if (!category) {
      return;
    }

    const {product: editedProduct} = massEditList[productIdxToAddCategory];
    const alreadyEdited = editedProducts.find((p) => p.sku === editedProduct.sku);
    const otherAttributes = alreadyEdited?.custom_attributes?.filter((a) => a.attribute_code !== 'tax_class_id') || [];
    const categoryIds = editedProduct.custom_attributes.find((a) => a.attribute_code === 'category_ids')?.value;

    if (categoryIds.includes(String(category.id))) {
      return;
    }

    const changedProductData = {
      sku: editedProduct.sku,
      custom_attributes: [...otherAttributes, {attribute_code: 'category_ids', value: [...categoryIds, String(category.id)]}]
    };

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((p) => {
        return p.sku === changedProductData.sku ? ({...p, ...changedProductData}) : p;
      }));
    } else {
      setEditedProducts([...editedProducts, changedProductData]);
    }

    const updatedList = massEditList.map((item, idx) => {
      if (idx == productIdxToAddCategory) {
        const {product = {}} = item;
        const {custom_attributes = []} = product;

        const updatedAttributes = custom_attributes.map((attribute) => {
          if (attribute.attribute_code === 'category_ids') {
            const newValue = [...attribute.value, String(category.id)];
            return {...attribute, value: newValue};
          }

          return attribute;
        });

        return {...item, product: { ...product, custom_attributes: updatedAttributes } };
      }

      return item;
    });

    dispatch(changeProductCategoryStatus(category.id));
    setMassEditList(updatedList);
    setChangedProductsIds([...new Set([...changedProductIds, productIdxToAddCategory])]);
  };

  const handleConfirmDelete = async () => {
    const tasks = massEditList.map((item) => {
      const task = generateTaskToDeleteProduct(
        item.product,
        userStore?.quoteProStoreId,
        userStore.storeId,
        userStore.storeCode
      );

      return task;
    });

    await addTasksToQueueApi({ dispatch, tasks });
    history.goBack();
  };

  const columns = [
    {
      name: 'product.name', // field name in the row object
      label: 'Name', // column title that will be shown in table
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({ style: { width: '200px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {product} = massEditList[dataIndex];
          return (
            <div className="flex items-center" key={product.sku}>
              <TextField
                required
                className="w-full"
                multiline
                name="name"
                defaultValue={product.name}
                onChange={(event) => handleChangeName(dataIndex, event.target.value)}
                error={!product.name.trim().length}
                helperText={!product.name.trim().length && 'Field is required'}
              />
            </div>
          );
        },
      },
    },
    {
      name: 'product.price',
      label: 'Price',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '200px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {product} = massEditList[dataIndex];
          const minProductPrice = Number(product?.custom_attributes?.find((item) => item.attribute_code === 'catalog_min_price')?.value);
          const lessThanMinPrice = Boolean(minProductPrice && product.price < minProductPrice);

          return (
            <NumberFormat
              key={product.sku}
              allowNegative={false}
              prefix="$"
              fixedDecimalScale
              decimalScale={2}
              thousandSeparator
              name="price"
              defaultValue={product.price}
              className="mx-2"
              inputProps={{className: 'text-center min-w-100' }}
              onValueChange={(val) => handleChangePrice(val, dataIndex, minProductPrice)}
              customInput={TextField}
              error={lessThanMinPrice}
              helperText={lessThanMinPrice && `MAP cannot go lower than $${minProductPrice}`}
            />
          );
        },
      },
    },
    {
      name: 'product.custom_attributes',
      label: 'Categories',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ className: 'w-540 m-0 p-0' }),
        setCellProps: () => ({ className: 'mb-5 mt-5 p-0' }),
        customBodyRenderLite: (dataIndex) => {
          const {product = {}} = massEditList[dataIndex];
          const {custom_attributes = []} = product;
          const categoryAttribute = custom_attributes.find((a) => a.attribute_code === 'category_ids') || {};
          const { value: productCategoriesIds } = categoryAttribute;

          return (
            <div key={product.sku}>
              <div className="flex items-center">
                <Button
                  color="primary"
                  variant="contained"
                  className="rounded mb-4"
                  onClick={() => {
                    setOpenAddCategoryModal(true);
                    setProductIdxToAddCategory(dataIndex);
                  }}
                >
                  <Icon>add</Icon>
                  Add Product To Category
                </Button>
              </div>
              {productCategoriesIds?.map((id) => {
                const nameWithPath = storeCategoriesWithActiveNavLevel?.find((category) => category.id == id)?.nameWithPath;

                if (!nameWithPath) {
                  return null;
                }

                return (
                  <Chip
                    key={id}
                    label={nameWithPath}
                    onDelete={() => handleRemoveCategory(dataIndex, id)}
                    className={clsx({
                      'm-1 p-2 text-14': true,
                    })}
                  />
                );
              })}
            </div>
          );
        },
      },
    },
    {
      name: 'product.extension_attributes.stock_item.qty',
      label: 'Inventory',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '200px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {product} = massEditList[dataIndex];
          const {qty} = product.extension_attributes.stock_item;
          const {type_id} = product;

          return type_id === 'simple'
            ? (
              <NumberFormat
                key={product.sku}
                allowNegative={false}
                name="qty"
                defaultValue={qty}
                className="mx-2"
                inputProps={{ className: 'text-center min-w-100' }}
                onValueChange={(val) => handleChangeQty(val, dataIndex)}
                customInput={TextField}
              />
            ) : null;
        }
      },
    },
    {
      name: 'product.custom_attributes',
      label: 'Is Taxable',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ className: 'w-140 m-0 p-0' }),
        customBodyRenderLite: (dataIndex) => {
          const {product = {}} = massEditList[dataIndex];
          const {custom_attributes = []} = product;
          const taxClassAttribute = custom_attributes.find((a) => a.attribute_code === 'tax_class_id') || {};
          const { value: productTaxClassId } = taxClassAttribute;

          return (
            <Switch
              key={product.sku}
              onChange={(event) => handleChangeProductTaxClass(dataIndex, event.target.checked)}
              checked={productTaxClassId === String(taxableClass?.class_id)}
              disabled={!taxableClass}
              color="primary"
              name="Taxable"
              className="ml-2"
            />
          );
        },
      },
    },
    {
      name: 'product.status',
      label: 'Status',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '100px' }, className: 'text-right pr-8 text-14' }),
        setCellProps: () => ({ className: 'text-right' }),
        customBodyRenderLite: (dataIndex) => {
          const {product = {}} = massEditList[dataIndex];
          const {status} = product;

          return (
            <Select
              key={product.sku}
              name="status"
              value={status == 1 ? '1' : '2'}
              label="Status"
              onChange={(event) => handleChangeStatus(dataIndex, Number(event.target.value))}
            >
              <MenuItem value="1">Enabled</MenuItem>
              <MenuItem value="2">Disabled</MenuItem>
            </Select>
          );
        },
      },
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '100px' }, className: 'text-right pr-8 text-14' }),
        setCellProps: () => ({ className: 'text-right' }),
        customBodyRenderLite: (dataIndex) => {
          const {product} = massEditList[dataIndex];
          const minProductPrice = Number(product?.custom_attributes?.find((item) => item.attribute_code === 'catalog_min_price')?.value);

          return (
            <Button
              key={product.sku}
              variant="contained"
              color="primary"
              title="Save"
              disabled={!changedProductIds?.includes(dataIndex) || !product.name.trim().length || product.price < 0 || product.price < minProductPrice}
              onClick={() => handleClickSave(dataIndex)}
            >
              Save
            </Button>
          );
        },
      },
    },
  ];

  return (
    <div className="flex-grow h-full scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Listings', path: '/store-products/list/active' },
            { name: 'Bulk Edit' }
          ]}
        />
      </div>
      <Grid container xs={12} className="flex px-8 pt-0 pb-4" justifyContent="flex-end">
        <Typography>
          Enable All:
          <Switch
            onChange={handleChangeStatusAll}
            checked={isAllEnabled}
            color="primary"
            className="ml-2 mr-2"
            title="Click here to change status for all products"
          />
        </Typography>
        {permissions.massEditDeleteAll && massEdit.productsType === 'draft' &&
        <Button
          type="button"
          color="primary"
          variant="contained"
          className="uppercase mr-2"
          title="Click here to delete all products in the list"
          onClick={() => setOpenDeleteDialog(true)}
          endIcon={<DeleteIcon color="inherit" />}
        >
          Remove all
        </Button>}
        {permissions.massEditSaveAll &&
        <Button
          type="button"
          color="primary"
          variant="contained"
          className="uppercase custom-check-all custom-check-all-select"
          title="Click here to save all products"
          onClick={handleSaveAll}
          disabled={!changedProductIds?.length || !!isEmptyProductName?.length || isPriceLowerThanMin}
        >
          Save All & go back
        </Button>}
      </Grid>
      <div className="mx-10">
        {loading && <LinearProgress className="mt-10" />}
        {!loading && massEditList?.length > 0 &&
        <Card>
          <MUIDataTable
            data={massEditList}
            columns={columns}
            options={{
              filterType: 'textField',
              responsive: 'standard',
              selectableRows: 'none',
              search: false,
              filter: false,
              print: false,
              download: false,
              enableNestedDataAccess: '.',
              rowsPerPageOptions: [10, 20, 40, 80, 100]
            }}
          />
          {!!storeCategoriesTree.length && openAddCategoryModel &&
            <AddCategoryModal
              itemName={massEditList[productIdxToAddCategory]?.product?.name}
              open={openAddCategoryModel}
              setOpen={setOpenAddCategoryModal}
              onConfirm={handleSelectCategories}
              isMassEdit
            />}
          <ConfirmDialog
            title="Confirm Changes?"
            open={openDeleteDialog}
            setOpen={() => setOpenDeleteDialog(true)}
            onConfirm={() => handleConfirmDelete()}
            onClose={() => setOpenDeleteDialog(false)}
          >
            Are you sure you want to remove these items in your Draft? Please know that once these items have been removed, they will no longer be available in your store.
          </ConfirmDialog>
        </Card>
        }
        <BackdropCircular loading={savingProduct || loadingTasksToQueue} />
      </div>
    </div>
  );
};

export default MassEditProducts;
